import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Chart as ChartJS,
  BarController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { useSelector } from "react-redux";

ChartJS.register(
  BarController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
);

// ─────────────────────────────────────────────────────────────
// 🎨 COLORS — exactly original
// ─────────────────────────────────────────────────────────────
// const LOCATION_COLORS = [
//   "#378ADD",
//   "#1D9E75",
//   "#D85A30",
//   "#D4537E",
//   "#BA7517",
//   "#534AB7",
//   "#888780",
// ];
const BASE_COLORS = [
  { h: 174, s: 52 },  
  { h: 207, s: 70 }, 
  { h: 230, s: 50 },
  { h: 191, s: 72 }, 
  { h: 240, s: 75 }, 
  { h: 145, s: 52 }, 
];

// ─────────────────────────────────────────────────────────────
// 🔧 HELPERS
// ─────────────────────────────────────────────────────────────
function getLocaleBySettings(priceFormat = {}) {
  if (priceFormat?.currency === "INR") return "en-IN";
  if (priceFormat?.thousand_separator === ".") return "nl-NL";
  return "en-US";
}

function getScaleConfig(priceFormat = {}) {
  const showAmountIn = String(
    priceFormat?.show_amount_in || "Thousand",
  ).toLowerCase();
  if (showAmountIn.includes("billion"))
    return { divisor: 1000000000, unitLabel: "Billion" };
  if (showAmountIn.includes("million"))
    return { divisor: 1000000, unitLabel: "Million" };
  if (showAmountIn.includes("lakh"))
    return { divisor: 100000, unitLabel: "Lakh" };
  if (showAmountIn.includes("crore"))
    return { divisor: 10000000, unitLabel: "Crore" };
  return { divisor: 1000, unitLabel: "Thousand" };
}

function formatAxisNumber(n, priceFormat = {}) {
  const locale = getLocaleBySettings(priceFormat);
  const decimal = Number(priceFormat?.decimal_point || 2);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimal,
  }).format(n);
}

// Nice step ticks with configurable scale unit.
function makeStepTicks(maxVal, priceFormat = {}, divisor = 1000) {
  if (!maxVal || maxVal <= 0) return {};
  const raw = maxVal / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = Math.ceil(raw / magnitude) * magnitude;
  const max = Math.ceil(maxVal / step) * step;
  return {
    min: 0,
    max,
    ticks: {
      stepSize: step,
      callback: (v) => formatAxisNumber(Number(v) / divisor, priceFormat),
    },
  };
}

// ─────────────────────────────────────────────────────────────
// 🔝 TOP N DROPDOWN — per chart, All / 10 / 20 / 30
// ─────────────────────────────────────────────────────────────
function TopNDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // only Top 10 / Top 20 / Top 30
  const choices = [
    ...[10, 20, 30].map((n) => ({ key: String(n), label: `Top ${n}` })),
  ];

  const selected = choices.find((c) => c.key === value) || choices[0];

  return (
    <div style={styles.topNWrapper} ref={ref}>
      <div style={styles.topNBtn} onClick={() => setOpen(!open)}>
        {selected.label}
        <span style={styles.topNChevron}>▾</span>
      </div>
      {open && (
        <div style={styles.topNMenu}>
          {choices.map((c) => (
            <div
              key={c.key}
              style={{
                ...styles.topNOption,
                background: value === c.key ? "#eef2f3" : "#fff",
                fontWeight: value === c.key ? 600 : 400,
              }}
              onClick={() => {
                onChange(c.key);
                setOpen(false);
              }}
            >
              {c.label}
              {value === c.key && (
                <span style={{ color: "#5CC7C1", marginLeft: "auto" }}>✓</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 🔘 BY AMOUNT/QTY DROPDOWN
// ─────────────────────────────────────────────────────────────
function ByTypeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div style={styles.byTypeWrapper} ref={ref}>
      <div style={styles.byTypeBtn} onClick={() => setOpen(!open)}>
        By {value} <span style={styles.byTypeChevron}>▾</span>
      </div>
      {open && (
        <div style={styles.byTypeMenu}>
          {["Amount", "Qty"].map((item) => (
            <div
              key={item}
              style={{
                ...styles.byTypeOption,
                background: value === item ? "#eef2f3" : "#fff",
                fontWeight: value === item ? 600 : 400,
              }}
              onClick={() => {
                onChange(item);
                setOpen(false);
              }}
            >
              {item}
              {value === item && (
                <span style={{ color: "#5CC7C1", marginLeft: "auto" }}>✓</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ⋮ THREE DOTS — print + download icons only
// ─────────────────────────────────────────────────────────────
function ThreeDotsMenu({ chartRef }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handlePrint = () => {
    window.print();
    setOpen(false);
  };
  const handleDownload = () => {
    const chart = chartRef?.current;
    if (!chart) return;
    const url = chart.toBase64Image();
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart.png";
    link.click();
    setOpen(false);
  };

  return (
    <div style={styles.menuWrapper} ref={ref}>
      <div style={styles.dots} onClick={() => setOpen(!open)}>
        ⋮
      </div>
      {open && (
        <div style={styles.menu}>
          <div style={styles.menuIconRow}>
            <div style={styles.menuIconBtn} onClick={handlePrint} title="Print">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
            </div>
            <div
              style={styles.menuIconBtn}
              onClick={handleDownload}
              title="Download"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
                <path d="M19 16 v3 M17.5 19 h3" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 🔧 slice rows by topN, sorted by sales desc
// ─────────────────────────────────────────────────────────────
function sliceRows(rows, topN) {
  const n = parseInt(topN, 10);
  if (isNaN(n) || n <= 0) return rows;
  return [...rows]
    .sort((a, b) => (Number(b.sales) || 0) - (Number(a.sales) || 0))
    .slice(0, n);
}

function makeBarOptions(
  maxVal,
  { stacked = false, byType = "Amount", priceFormat = {} } = {},
) {
  const currency = priceFormat?.currency || "USD";
  const { divisor, unitLabel } = getScaleConfig(priceFormat);
  const yTitle = byType === "Amount" ? `${currency} (${unitLabel})` : "Qty";
  const stepTickConfig =
    byType === "Amount"
      ? makeStepTicks(maxVal, priceFormat, divisor)
      : makeStepTicks(maxVal, { decimal_point: 0 }, 1);
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 4, right: 8, bottom: 0, left: 0 },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rectRounded",
          boxWidth: 8,
          boxHeight: 8,
          color: "#6b7280",
          padding: 12,
          font: { size: 10 },
        },
      },
    },
    scales: {
      x: {
        stacked,
        grid: { display: false, drawBorder: false },
        ticks: {
          autoSkip: false,
          maxRotation: 90,
          minRotation: 90,
          font: { size: 10 },
          color: "#7b7b7b",
        },
      },
      y: {
        stacked,
        beginAtZero: true,
        ...stepTickConfig,
        grid: { color: "rgba(0,0,0,0.08)", drawBorder: false },
        title: {
          display: true,
          text: yTitle,
          color: "#4b5563",
          font: { size: 12, weight: 600 },
          padding: { bottom: 4 },
        },
        ticks: {
          ...(stepTickConfig.ticks || {}),
          color: "#7b7b7b",
          font: { size: 10 },
        },
      },
    },
  };
}

// ─────────────────────────────────────────────────────────────
// 📊 1. ALL LOCATION TARGET VS SALES
// ─────────────────────────────────────────────────────────────
export function SalesTargetChart({
  rows = [],
  priceFormat = {},
  onDragStart,
  onDragEnd,
}) {
  const chartRef = useRef();
  const [byType, setByType] = useState("Amount");
  const totalSales = rows.reduce(
    (sum, r) =>
      sum + (byType === "Amount" ? Number(r.sales) || 0 : Number(r.qty) || 0),
    0,
  );
  const totalTarget = rows.reduce(
    (sum, r) =>
      sum + (byType === "Amount" ? Number(r.target) || 0 : Number(r.qty) || 0),
    0,
  );
  const allVals = [totalSales, totalTarget];
  const maxVal = Math.max(...allVals, 0);

  const data = {
    labels: ["0"],
    datasets: [
      {
        label: "Target",
        data: [totalTarget],
        backgroundColor: "#55c0bb",
        borderRadius: 0,
        barThickness: 24,
      },
      {
        label: "Sales",
        data: [totalSales],
        backgroundColor: "#7f96dd",
        borderRadius: 0,
        barThickness: 24,
      },
    ],
  };

  const options = makeBarOptions(maxVal, { byType, priceFormat });

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          title="Drag to reorder chart"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>All Location target and sales</p>
        <div style={styles.headerRight}>
          <ByTypeDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <Bar ref={chartRef} data={data} options={options} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 📊 2. LOCATION COMPARISON TARGET VS SALES
// ─────────────────────────────────────────────────────────────
export function ProfitChart({
  rows = [],
  priceFormat = {},
  onDragStart,
  onDragEnd,
}) {
  const chartRef = useRef();
  const [byType, setByType] = useState("Amount");
  const [topN, setTopN] = useState("10");

  const sliced = sliceRows(rows, topN);
  const allVals = sliced.flatMap((r) =>
    byType === "Amount"
      ? [Number(r.target) || 0, Number(r.sales) || 0]
      : [Number(r.qty) || 0]
  );
  const maxVal = Math.max(...allVals, 0);

  const data = {
    labels: sliced.map((r) => r.location),
    datasets: [
      {
        label: "Target",
        data: sliced.map((r) =>
          byType === "Amount" ? Number(r.target) || 0 : Number(r.qty) || 0,
        ),
        backgroundColor: "#5CC7C1",
        borderRadius: 0,
        barThickness: 8,
      },
      {
        label: "Sales",
        data: sliced.map((r) =>
          byType === "Amount" ? Number(r.sales) || 0 : Number(r.qty) || 0,
        ),
        backgroundColor: "#5C74C7",
        borderRadius: 0,
        barThickness: 8,
      },
    ],
  };

  const options = makeBarOptions(maxVal, { byType, priceFormat });

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          title="Drag to reorder chart"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>Location Comparing target and sales</p>
        <div style={styles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} />
          <ByTypeDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <Bar ref={chartRef} data={data} options={options} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 🍩 3. TOP SALES LOCATION DONUT
// ─────────────────────────────────────────────────────────────
export function CustomerDonutChart({ rows = [], onDragStart, onDragEnd }) {
  const chartRef = useRef();
  const [byType, setByType] = useState("Amount");
  const [topN, setTopN] = useState("10");

  const sliced = sliceRows(rows, topN);
  // const colors = sliced.map((_, i) => LOCATION_COLORS[i % LOCATION_COLORS.length]);
const colors = useMemo(() => {
  return sliced.map((_, i) => {
    const base = BASE_COLORS[i % BASE_COLORS.length];
    const shade = Math.floor(i / BASE_COLORS.length);
    // Each cycle shifts lightness: 55% → 65% → 45% → 70%
    const lightSteps = [55, 65, 45, 70, 40, 75];
    const l = lightSteps[shade % lightSteps.length];
    return `hsl(${base.h}, ${base.s}%, ${l}%)`;
  });
}, [rows.length]); // stable — only changes if row count changes
  const data = {
    labels: sliced.map((r) => r.location),
    datasets: [
      {
        data: sliced.map((r) =>
          byType === "Amount" ? Number(r.sales) || 0 : Number(r.qty) || 0,
        ),
        backgroundColor: colors,
        borderWidth: 0,
        cutout: "68%",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8,
          color: "#7b7b7b",
          padding: 10,
          font: { size: 8 },
        },
      },
    },
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          title="Drag to reorder chart"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>Top Sales Location</p>
        <div style={styles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} />
          <ByTypeDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <Doughnut ref={chartRef} data={data} options={options} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 📊 4. COST / SALES / PROFIT
// ─────────────────────────────────────────────────────────────
export function NewCustomerChart({
  rows = [],
  priceFormat = {},
  onDragStart,
  onDragEnd,
}) {
  const chartRef = useRef();
  const [byType, setByType] = useState("Amount");
  const [topN, setTopN] = useState("10");

  const sliced = sliceRows(rows, topN);
  const allVals = sliced.flatMap((r) =>
    byType === "Amount"
      ? [
          Number(r.cost) || 0,
          Number(r.sales) || 0,
          Math.abs(Number(r.profit) || 0),
        ]
      : [Number(r.qty) || 0]
  );
  const maxVal = Math.max(...allVals, 0);

  const data = {
    labels: sliced.map((r) => r.location),
    datasets: [
      {
        label: "Cost",
        data: sliced.map((r) =>
          byType === "Amount" ? Number(r.cost) || 0 : Number(r.qty) || 0,
        ),
        backgroundColor: "#5CC7C1",
        borderRadius: 0,
        barThickness: 6,
      },
      {
        label: "Sales",
        data: sliced.map((r) =>
          byType === "Amount" ? Number(r.sales) || 0 : Number(r.qty) || 0,
        ),
        backgroundColor: "#5C74C7",
        borderRadius: 0,
        barThickness: 6,
      },
      {
        label: "Profits",
        data: sliced.map((r) =>
          byType === "Amount" ? Number(r.profit) || 0 : Number(r.qty) || 0,
        ),
        backgroundColor: "#52ABE1",
        borderRadius: 0,
        barThickness: 6,
      },
    ],
  };

  const options = makeBarOptions(maxVal, { byType, priceFormat });

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          title="Drag to reorder chart"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>Comparing Cost, Sales and Profits</p>
        <div style={styles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} />
          <ByTypeDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <Bar ref={chartRef} data={data} options={options} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 📊 MAIN — 2 column layout: row1 [chart1, chart2] row2 [chart3, chart4]
// ─────────────────────────────────────────────────────────────
export default function BriefSummaryCharts({ rows = [] }) {
  const priceFormat = useSelector(
    (state) => state?.app?.organisation_settings?.price_format || {},
  );
  const safeRows = rows.filter((r) => r && r.location);
  const [chartOrder, setChartOrder] = useState([
    "salesTarget",
    "locationCompare",
    "costSalesProfit",
    "topSalesLocation",
  ]);
  const [draggingChart, setDraggingChart] = useState(null);

  const renderChart = {
    salesTarget: (cardId) => (
      <SalesTargetChart
        rows={safeRows}
        priceFormat={priceFormat}
        onDragStart={() => handleDragStart(cardId)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
    locationCompare: (cardId) => (
      <ProfitChart
        rows={safeRows}
        priceFormat={priceFormat}
        onDragStart={() => handleDragStart(cardId)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
    costSalesProfit: (cardId) => (
      <NewCustomerChart
        rows={safeRows}
        priceFormat={priceFormat}
        onDragStart={() => handleDragStart(cardId)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
    topSalesLocation: (cardId) => (
      <CustomerDonutChart
        rows={safeRows}
        onDragStart={() => handleDragStart(cardId)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
  };

  function handleDragStart(cardId) {
    setDraggingChart(cardId);
  }

  function handleDrop(targetId) {
    if (!draggingChart || draggingChart === targetId) return;
    const next = [...chartOrder];
    const fromIdx = next.indexOf(draggingChart);
    const toIdx = next.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setChartOrder(next);
    setDraggingChart(null);
  }

  return (
    <div style={styles.root}>
      <div style={styles.grid}>
        {chartOrder.map((cardId) => (
          <div
            key={cardId}
            style={styles.colItem}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(cardId)}
          >
            {renderChart[cardId](cardId)}
          </div>
        ))}
      </div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => setDraggingChart(null)}
        style={styles.dropSafe}
      />
    </div>
  );
}

// Keep reference for possible responsive fallback.
const twoColRowDeprecated = {
  display: "flex",
  gap: 8,
  marginBottom: 8,
};

const colItemDeprecated = {
  flex: 1,
  minWidth: 0,
};

const styles = {
  root: {
    padding: "0 20px 16px",
    background:'#fff'
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },
  colItem: {
    minWidth: 0,
    position: "relative",
  },
  dragHandle: {
    cursor: "grab",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "2px 3px",
    lineHeight: 0,
    flexShrink: 0,
  },
  dropSafe: {
    height: 0,
  },
  twoColRow: twoColRowDeprecated,
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "10px 12px 8px",
    height: "100%",
    boxSizing: "border-box",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    marginBottom: 2,
    gap: 8,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#606060",
    margin: '10px 2px',
    flex: 1,
    fontFamily:'Segoe UI',
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "left",
  },
  chartWrap: {
    height: 440,
  },

  // TOP N DROPDOWN
  topNWrapper: { position: "relative",fontFamily:'Segoe UI', },
  topNBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#fff",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily:'Segoe UI',
  },
  topNChevron: { fontSize: 14, color: "#606060" },
  topNMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: 130,
    zIndex: 20,
    overflow: "hidden",
    fontFamily:'Segoe UI',
  },
  topNOption: {
    display: "flex",
    alignItems: "center",
    padding: "9px 14px",
    fontSize: 14,
    cursor: "pointer",
    color: "#606060",
    fontFamily:'Segoe UI',
    gap: 4,
  },

  // BY TYPE DROPDOWN
  byTypeWrapper: { position: "relative",fontFamily:'Segoe UI', },
  byTypeBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#fff",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily:'Segoe UI',
  },
  byTypeChevron: { fontSize: 13, color: "#606060" },
  byTypeMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: 130,
    zIndex: 20,
    overflow: "hidden",
  },
  byTypeOption: {
    display: "flex",
    alignItems: "center",
    padding: "9px 14px",
    fontSize: 13,
    cursor: "pointer",
    color: "#333",
    gap: 4,
  },

  // THREE DOTS
  menuWrapper: { position: "relative" },
  dots: {
    cursor: "pointer",
    fontSize: 22,
    color: "#606060",
    padding: "0 4px",
    lineHeight: 1,
    userSelect: "none",
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 6px)",
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 10,
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    zIndex: 30,
    padding: "10px 14px",
  },
  menuIconRow: {
    display: "flex",
    gap: 16,
    alignItems: "center",
  },
  menuIconBtn: {
    cursor: "pointer",
    padding: 6,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};
